import { HTMLAttributes, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import type { NavChildren, NavigationA } from "@components/Header/types";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useGetMainNavList } from "@hooks/useGetMainNavList";
import { cn } from "@util/helpers";

import { navigationConstant } from "./constant";

export type NavigationProps = {
  data?: NavigationA;
  position?: string;
};

export function Navigation({
  data,
  position,
}: NavigationProps): React.ReactElement {
  const navigationData = data?.length ? data : navigationConstant;
  const findMenuData = useGetMainNavList({ navigationData, position });

  return (
    <nav aria-label={`header-nav-${position}`} className="flex gap-3">
      {findMenuData?.map((item, index) => {
        return (
          <NavItem
            item={item}
            index={index}
            key={item.key}
            position={position}
          />
        );
      })}
    </nav>
  );
}

type NavItemProps = {
  item: NavChildren;
  position?: string;
  index?: number;
} & Pick<HTMLAttributes<HTMLDivElement>, "className">;

const navItemStyle = cn(
  "p-3 duration-300",
  "lg:hover:bg-gray-250 rounded-[25px]"
);

export function NavItem({ item, className }: NavItemProps): React.ReactElement {
  const { label, link, target, extraClass, trackingData }: NavChildren = item;
  const [isInView, setInView] = useState(true);
  const sendDataToGTM = useGTMDispatch();

  const { ref } = useInView({
    threshold: 1,
    onChange: (_, entry) => {
      setInView(entry?.intersectionRatio >= 1);
    },
  });

  const onClick = () => {
    if (trackingData) {
      sendDataToGTM(trackingData);
    }
  };

  return (
    <Link
      ref={ref}
      data-testid={`navigation-${label.toLowerCase()}`}
      role="link"
      to={`${link}?ref=header`}
      className={cn(
        "group invisible relative flex-none font-poppins text-2xs font-semibold leading-none tracking-tightest text-gray-850",
        navItemStyle,
        className,
        { visible: isInView },
        extraClass
      )}
      target={target ? target : undefined}
      reloadDocument
      onClick={onClick}
    >
      {label}
    </Link>
  );
}
